import { useEffect } from "react"
import { env } from "../config/env"
import {
  ANALYTIC_TYPE_LABELS,
  loadViewMediaScript,
} from "../utils/viewMediaPlayer"

/*
  TEMP (ViewMedica discovery): logs what the player actually sends, so the
  intro -> main video handover can be detected from real data. Remove once
  the intro detector is built.

  Never in production: it runs everywhere except a build pointed at the
  production API (development and staging). Runs in preview too, unlike the
  tracking, since staging is often opened through the admin preview link.

  Logged, per video, prefixed [vm-discovery]:
  - every message the player iframe posts (analytics types 0-8 with their
    payloads, and the undocumented { event, payload } channel), except
    replies to API calls
  - player state / duration changes, from a 1 s poll
  - a snapshot of the playlist and item lookups at start-up and whenever
    the duration changes or a playlist analytic arrives
  - once per page: tab visibility, pagehide/pageshow and each sendBeacon
    call (see installPageLogger)
*/
const PRODUCTION_API_HOST = "dashboard-api.expertradiology.com"
const POLL_MS = 1000
const CALL_TIMEOUT_MS = 2000

const isEnabled = () => {
  try {
    return new URL(env.REACT_APP_BASE_URL).hostname !== PRODUCTION_API_HOST
  } catch {
    return false
  }
}

const withTimeout = (promise) =>
  Promise.race([
    promise,
    new Promise((resolve) =>
      setTimeout(() => resolve("<no answer>"), CALL_TIMEOUT_MS)
    ),
  ]).catch((error) => `<error: ${error?.message || error}>`)

const compact = (value) => {
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

/*
  Page-level lines, installed once however many players there are: tab
  visibility, pagehide, and every navigator.sendBeacon call the event queue
  makes (wrapped, so its size and whether the browser accepted it are
  logged synchronously — the page may be gone a moment later).
*/
let pageLoggerInstalled = false

const installPageLogger = () => {
  if (pageLoggerInstalled) return
  pageLoggerInstalled = true

  const startedAt = performance.now()
  const log = (...args) => {
    const seconds = ((performance.now() - startedAt) / 1000).toFixed(1)
    // eslint-disable-next-line no-console
    console.info(`[vm-discovery page +${seconds}s]`, ...args)
  }

  log(`VISIBILITY ${document.visibilityState} (at start)`)
  document.addEventListener("visibilitychange", () =>
    log(`VISIBILITY ${document.visibilityState}`)
  )
  window.addEventListener("pagehide", (event) =>
    log(`PAGEHIDE persisted=${event.persisted}`)
  )
  window.addEventListener("pageshow", (event) =>
    log(`PAGESHOW persisted=${event.persisted}`)
  )

  if (navigator.sendBeacon) {
    const sendBeacon = navigator.sendBeacon.bind(navigator)
    navigator.sendBeacon = (url, data) => {
      const queued = sendBeacon(url, data)
      const path = String(url).replace(/^https?:\/\/[^/]+/, "")
      log(`BEACON queued=${queued} bytes=${data?.size ?? "?"} -> ${path}`)
      return queued
    }
  } else {
    log("BEACON unsupported; the queue will use fetch keepalive")
  }
}

export const useViewMediaDiscovery = ({ iframeRef, videoId }) => {
  useEffect(() => {
    if (!isEnabled() || !iframeRef.current) return

    installPageLogger()

    const iframe = iframeRef.current
    const startedAt = performance.now()
    let api = null
    let pollId = null
    let cancelled = false
    let last = { state: undefined, duration: undefined }

    const log = (...args) => {
      const seconds = ((performance.now() - startedAt) / 1000).toFixed(1)
      // eslint-disable-next-line no-console
      console.info(`[vm-discovery v${videoId} +${seconds}s]`, ...args)
    }

    const snapshot = async (why) => {
      if (!api) return
      const [index, items, playlist, primary, current, time] =
        await Promise.all([
          withTimeout(api.getCurrentPlaylistIndex()),
          withTimeout(api.getCurrentPlaylistItems()),
          withTimeout(api.getCurrentPlaylist()),
          withTimeout(api.getPrimaryItem()),
          withTimeout(api.getCurrentItem()),
          withTimeout(api.getCurrentTime()),
        ])
      if (cancelled) return
      // One JSON string, not an object: console readers that don't expand
      // objects (Claude in Chrome's among them) would show only "Object".
      log(
        `SNAPSHOT (${why}) ${compact({
          playlistIndex: index,
          playlistItems: items,
          playlist,
          primaryItem: primary,
          currentItem: current,
          currentTime: time,
          duration: last.duration,
          state: last.state,
        })}`
      )
    }

    const handleMessage = (event) => {
      if (event.source !== iframe.contentWindow) return
      const data = event.data || {}
      // Replies to API calls ({ id, payload }) — the snapshot logs those.
      if (data.id !== undefined && data.analyticType === undefined) return

      if (data.analyticType !== undefined) {
        const label = ANALYTIC_TYPE_LABELS[data.analyticType] || "unknown"
        log(`ANALYTIC ${data.analyticType} ${label}`, compact(data.payload))
        // Playlist started / item reached / ended may mark the handover.
        if ([6, 7, 8].includes(data.analyticType)) snapshot(`analytic ${label}`)
        return
      }

      if (data.event !== undefined) {
        log(`EVENT "${data.event}"`, compact(data.payload))
        return
      }

      log("OTHER MESSAGE", compact(data))
    }

    window.addEventListener("message", handleMessage)

    const boot = () => {
      if (cancelled || api) return
      api = new window.OndemandPlayerApi({ frame: iframe })
      log("player API attached")
      snapshot("start-up")

      pollId = setInterval(async () => {
        const [state, duration] = await Promise.all([
          withTimeout(api.getPlayerState()),
          withTimeout(api.getDuration()),
        ])
        if (cancelled) return

        const durationChanged = !Object.is(duration, last.duration)
        if (state !== last.state || durationChanged) {
          log(`STATE ${last.state} -> ${state}, duration ${duration}`)
        }
        last = { state, duration }
        if (durationChanged) snapshot("duration changed")
      }, POLL_MS)
    }

    loadViewMediaScript()
      .then(() => {
        if (cancelled) return
        iframe.addEventListener("load", boot)
        setTimeout(boot, 1500)
      })
      .catch((error) => log("vm.js failed to load", error?.message))

    return () => {
      cancelled = true
      window.removeEventListener("message", handleMessage)
      iframe.removeEventListener("load", boot)
      if (pollId) clearInterval(pollId)
    }
  }, [iframeRef, videoId])
}
